import { createBrowserRouter, RouterProvider } from "react-router-dom";
import Layout from "./pages/Layout";
import NotFound from "./pages/NotFound";
import PrivateRoutes from "./routes/PrivateRoutes";
import Users from "./pages/Users";
import UserGroups from "./pages/UserGroups";
import ReactRouterErrorPage from "./pages/ReactRouterErrorPage";
import Login from "./pages/Login";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense, useState } from "react";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { trpc, trpcClient } from "./lib/trpc";
import InitPage from "./pages/InitPage";
import Connectors from "./pages/Connectors";
import Settings from "./pages/Settings";
import ChatLayout from "./pages/ChatLayout";
import Chat from "./pages/Chat";
import AdminRoutes from "./routes/AdminRoutes";
import Loading from "./components/loading/Loading";
import AppSetupCompleteRoutes from "./routes/AppSetupCompleteRoutes";
import AppSetupNotCompleteRoutes from "./routes/AppSetupNotCompleteRoutes";
import { ThemeProvider } from "./components/ThemeProvider";
import Dashboard from "./pages/Dashboard";
import Licenses from "./pages/Licenses";
import EmbeddingsViewer from "./pages/EmbeddingsViewer";
import AcceptInvitation from "./pages/AcceptInvitation";

// i18n is already initialized in i18n.ts with localStorage locale

const AgentManagement = lazy(() => import("./pages/agents/AgentManagement"));

const router = createBrowserRouter([
    {
        path: "/login",
        element: <Login />,
    },
    {
        path: "/accept-invitation",
        element: <AcceptInvitation />,
    },
    {
        path: "/",
        element: <PrivateRoutes />,
        errorElement: <ReactRouterErrorPage />,
        children: [
            {
                path: "/setup",
                element: <AppSetupNotCompleteRoutes />,
                errorElement: <ReactRouterErrorPage />,
                children: [
                    {
                        path: "/setup",
                        element: <InitPage />,
                    },
                ],
            },
            {
                path: "/",
                element: <AppSetupCompleteRoutes />,
                errorElement: <ReactRouterErrorPage />,
                children: [
                    {
                        path: "/",
                        element: <ChatLayout />,
                        children: [
                            {
                                path: "/",
                                element: <Chat />,
                            },
                            {
                                path: "/chats/:threadId",
                                element: <Chat />,
                            },
                        ],
                    },
                    {
                        path: "/admin",
                        element: <AdminRoutes />,
                        children: [
                            {
                                path: "/admin",
                                element: <Layout />,
                                children: [
                                    {
                                        path: "",
                                        element: <Dashboard />,
                                    },
                                    {
                                        path: "/admin/dashboard",
                                        element: <Dashboard />,
                                    },
                                    {
                                        path: "/admin/user-management/users",
                                        element: <Users />,
                                    },
                                    {
                                        path: "/admin/user-management/groups",
                                        element: <UserGroups />,
                                    },
                                    {
                                        path: "/admin/ai-configuration/connectors",
                                        children: [
                                            {
                                                path: "/admin/ai-configuration/connectors",
                                                element: <Connectors />,
                                            },
                                            {
                                                path: "/admin/ai-configuration/connectors/:agentType",
                                                element: (
                                                    <Suspense
                                                        fallback={
                                                            <div className="absolute inset-0 flex items-center justify-center">
                                                                <Loading />
                                                            </div>
                                                        }
                                                    >
                                                        <AgentManagement />
                                                    </Suspense>
                                                ),
                                            },
                                        ],
                                    },
                                    {
                                        path: "/admin/ai-configuration/vector-store",
                                        element: <EmbeddingsViewer />,
                                    },
                                    {
                                        path: "/admin/ai-configuration/:setting",
                                        element: <Settings />,
                                    },
                                    {
                                        path: "/admin/application-settings/:setting",
                                        element: <Settings />,
                                    },
                                    {
                                        path: "/admin/application-settings",
                                        element: <Settings />,
                                    },
                                    {
                                        path: "/admin/licenses",
                                        element: <Licenses />,
                                    },
                                    {
                                        path: "*",
                                        element: <NotFound />,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        path: "*",
        element: <NotFound />,
    },
]);

const App = () => {
    const [queryClient] = useState(
        () =>
            new QueryClient({
                defaultOptions: {
                    queries: {
                        refetchOnWindowFocus: false,
                        // staleTime: 1000 * 60, // 60 seconds - cache time
                        staleTime: Infinity,
                        // refetchOnMount: false,
                        retry: (failureCount, error) => {
                            if (
                                error &&
                                [
                                    "BAD_REQUEST", //400
                                    "UNAUTHORIZED", //401
                                    "FORBIDDEN", //403
                                    "NOT_FOUND", //404
                                ].some((statusCode) => error.message.includes(statusCode))
                            )
                                return false;
                            else if (failureCount < 5) return true;
                            else return false;
                        },
                    },
                },
            })
    );
    const [_trpcClient] = useState(() => trpcClient);
    return (
        <trpc.Provider client={_trpcClient} queryClient={queryClient}>
            <QueryClientProvider client={queryClient}>
                <ThemeProvider defaultTheme="system" storageKey="vite-ui-theme">
                    <RouterProvider router={router} />
                    <ReactQueryDevtools initialIsOpen={false} />
                </ThemeProvider>
            </QueryClientProvider>
        </trpc.Provider>
    );
};

export default App;
